/** Formative titration exercises; the teacher remains responsible for assessment. */
export const TITRATION_LABEL_SLOTS = ['burette', 'titrant', 'tap', 'vessel', 'sample', 'stirrer', 'stirbar', 'support'] as const;
export type TitrationLabelSlot = typeof TITRATION_LABEL_SLOTS[number];
export type TitrationLabelingConfig = {
  kind: 'colorimetric-titration';
  title?: string;
  requireTitle?: boolean;
  slots: TitrationLabelSlot[];
  labels: {id: string; text: string}[];
  /** Public key for immediate formative feedback; not a teacher grade. */
  expectedPlacements: Partial<Record<TitrationLabelSlot, string>>;
};
export type TitrationLabelingAnswer = {
  kind: 'titration-labeling';
  version: 1;
  title: string;
  placements: Partial<Record<TitrationLabelSlot, string>>;
  validated: boolean;
};
export type ChoiceHintConfig = {
  prompt: string;
  options: {id: string; text: string}[];
  correctOptionId: string;
  explanation: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const isId = (value: unknown): value is string => typeof value === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/.test(value);
const isText = (value: unknown, maximum: number): value is string => typeof value === 'string' && !!value.trim() && value.length <= maximum;
const isSlot = (value: unknown): value is TitrationLabelSlot => typeof value === 'string' && (TITRATION_LABEL_SLOTS as readonly string[]).includes(value);

export function titrationLabelingErrors(config: TitrationLabelingConfig): string[] {
  if (!config || config.kind !== 'colorimetric-titration' || !Array.isArray(config.slots) || !Array.isArray(config.labels) || !isRecord(config.expectedPlacements)) return ['Exercice de légende du titrage invalide.'];
  const errors: string[] = [];
  if (config.title !== undefined && !isText(config.title, 240)) errors.push('Titre du schéma invalide.');
  if (config.requireTitle !== undefined && typeof config.requireTitle !== 'boolean') errors.push('Consigne de titre du schéma invalide.');
  if (config.slots.length < 3 || config.slots.length > TITRATION_LABEL_SLOTS.length || config.slots.some(slot => !isSlot(slot)) || new Set(config.slots).size !== config.slots.length) errors.push('Emplacements du schéma invalides.');
  if (config.labels.length !== config.slots.length || config.labels.some(label => !label || !isId(label.id) || !isText(label.text, 240)) || new Set(config.labels.map(label => label?.id)).size !== config.labels.length) errors.push('Étiquettes du schéma invalides.');
  const expected = Object.entries(config.expectedPlacements);
  const labelIds = new Set(config.labels.map(label => label?.id));
  if (expected.length !== config.slots.length || expected.some(([slot, label]) => !config.slots.includes(slot as TitrationLabelSlot) || !labelIds.has(label)) || new Set(expected.map(([, label]) => label)).size !== expected.length) errors.push('Chaque emplacement doit correspondre à une étiquette distincte.');
  return errors;
}

export function choiceHintErrors(config: ChoiceHintConfig): string[] {
  if (!config || !isText(config.prompt, 1500) || !isText(config.explanation, 3000) || !Array.isArray(config.options)) return ['Aide à choix invalide.'];
  if (config.options.length < 2 || config.options.length > 6 || config.options.some(option => !option || !isId(option.id) || !isText(option.text, 1000)) || new Set(config.options.map(option => option?.id)).size !== config.options.length) return ['Propositions de l’aide invalides.'];
  if (!config.options.some(option => option.id === config.correctOptionId)) return ['Réponse de vérification de l’aide inconnue.'];
  return [];
}

export function checkChoiceHint(config: ChoiceHintConfig, optionId: string): boolean {
  return !choiceHintErrors(config).length && optionId === config.correctOptionId;
}

export function parseTitrationLabelingAnswer(value: string): TitrationLabelingAnswer {
  const empty: TitrationLabelingAnswer = {kind: 'titration-labeling', version: 1, title: '', placements: {}, validated: false};
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed) || parsed.kind !== empty.kind || parsed.version !== 1 || !isRecord(parsed.placements)) return empty;
    const placements: TitrationLabelingAnswer['placements'] = {};
    for (const [slot, label] of Object.entries(parsed.placements)) if (isSlot(slot) && isId(label)) placements[slot] = label;
    return {...empty, title: typeof parsed.title === 'string' ? parsed.title : '', placements, validated: parsed.validated === true};
  } catch { return empty; }
}

export function encodeTitrationLabelingAnswer(answer: TitrationLabelingAnswer): string {
  const placements = Object.fromEntries(TITRATION_LABEL_SLOTS.filter(slot => typeof answer.placements[slot] === 'string').map(slot => [slot, answer.placements[slot]]));
  return JSON.stringify({kind: 'titration-labeling', version: 1, title: answer.title, placements, validated: answer.validated === true});
}

export function checkTitrationLabeling(config: TitrationLabelingConfig, answer: TitrationLabelingAnswer): {complete: boolean; correct: boolean; correctCount: number; total: number; incorrectSlots: TitrationLabelSlot[]} {
  const slots = Array.isArray(config?.slots) ? config.slots : [];
  if (titrationLabelingErrors(config).length) return {complete: false, correct: false, correctCount: 0, total: slots.length, incorrectSlots: [...slots]};
  const placed = Object.values(answer.placements);
  const complete = placed.length === slots.length && Object.keys(answer.placements).every(slot => slots.includes(slot as TitrationLabelSlot)) && slots.every(slot => config.labels.some(label => label.id === answer.placements[slot])) && new Set(placed).size === slots.length && (!config.requireTitle || !!answer.title.trim());
  const incorrectSlots = slots.filter(slot => answer.placements[slot] !== config.expectedPlacements[slot]);
  const correctCount = slots.length - incorrectSlots.length;
  return {complete, correct: complete && !incorrectSlots.length, correctCount, total: slots.length, incorrectSlots};
}

/** Accept only the student's placement data; never trust a client-supplied grade. */
export function titrationLabelingAnswerError(value: string, config: TitrationLabelingConfig): string | null {
  if (!value) return null;
  let parsed: unknown;
  try { parsed = JSON.parse(value); } catch { return 'Réponse au schéma illisible.'; }
  if (!isRecord(parsed) || Object.keys(parsed).some(key => !['kind', 'version', 'title', 'placements', 'validated'].includes(key)) || parsed.kind !== 'titration-labeling' || parsed.version !== 1 || typeof parsed.title !== 'string' || parsed.title.length > 240 || typeof parsed.validated !== 'boolean' || !isRecord(parsed.placements)) return 'Réponse au schéma invalide.';
  const placements = Object.entries(parsed.placements);
  if (placements.some(([slot, label]) => !config.slots.includes(slot as TitrationLabelSlot) || !config.labels.some(option => option.id === label)) || new Set(placements.map(([, label]) => label)).size !== placements.length) return 'Emplacement ou étiquette invalide dans la réponse.';
  if (parsed.validated && !checkTitrationLabeling(config, parseTitrationLabelingAnswer(value)).complete) return 'Complète les étiquettes et le titre avant de valider.';
  return null;
}
